import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Swords, Plus, Trash2, Edit2, Search, Pause, Play, Tag, Link2, Camera, ShoppingBag, MousePointer } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';

const QUEST_TYPE_ICONS = {
  screenshot: Camera,
  manual: MousePointer,
  link_visit: Link2,
  purchase: ShoppingBag,
};

const QUEST_TYPE_LABELS = {
  screenshot: 'Screenshot',
  manual: 'Manual',
  link_visit: 'Link Visit',
  purchase: 'Purchase',
};

export default function AdminQuests() {
  const { token, API } = useAuth();
  const [quests, setQuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingQuest, setEditingQuest] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', points: '', quest_type: 'screenshot',
    per_user_limit: '1', thumbnail_url: '', attached_code: '',
    link_url: '', min_order_value: '', product_url: '', promo_code: '', discount_percentage: ''
  });

  const fetchQuests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/quests`, { headers: { Authorization: `Bearer ${token}` } });
      setQuests(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [API, token]);

  useEffect(() => { fetchQuests(); }, [fetchQuests]);

  const resetForm = () => {
    setForm({
      title: '', description: '', points: '', quest_type: 'screenshot',
      per_user_limit: '1', thumbnail_url: '', attached_code: '',
      link_url: '', min_order_value: '', product_url: '', promo_code: '', discount_percentage: ''
    });
    setEditingQuest(null);
  };

  const openEdit = (q) => {
    setForm({
      title: q.title, description: q.description, points: String(q.points),
      quest_type: q.quest_type, per_user_limit: String(q.per_user_limit),
      thumbnail_url: q.thumbnail_url || '', attached_code: q.attached_code || '',
      link_url: q.link_url || '',
      min_order_value: String(q.purchase_config?.min_order_value || ''),
      product_url: q.purchase_config?.product_url || '',
      promo_code: q.purchase_config?.promo_code || '',
      discount_percentage: String(q.purchase_config?.discount_percentage || ''),
    });
    setEditingQuest(q);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title,
      description: form.description,
      points: parseInt(form.points) || 0,
      quest_type: form.quest_type,
      per_user_limit: parseInt(form.per_user_limit) || 1,
      thumbnail_url: form.thumbnail_url,
      attached_code: form.attached_code,
      link_url: form.link_url,
      purchase_config: form.quest_type === 'purchase' ? {
        min_order_value: parseFloat(form.min_order_value) || 0,
        product_url: form.product_url,
        promo_code: form.promo_code,
        discount_percentage: parseFloat(form.discount_percentage) || 0,
      } : {},
    };
    try {
      if (editingQuest) {
        await axios.put(`${API}/admin/quests/${editingQuest.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/admin/quests`, payload, { headers: { Authorization: `Bearer ${token}` } });
      }
      resetForm();
      setShowForm(false);
      fetchQuests();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const toggleQuest = async (q) => {
    try {
      await axios.put(`${API}/admin/quests/${q.id}/toggle`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchQuests();
    } catch (e) { alert('Failed'); }
  };

  const deleteQuest = async (id) => {
    if (!window.confirm('Delete this quest and all submissions?')) return;
    try {
      await axios.delete(`${API}/admin/quests/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchQuests();
    } catch (e) { alert('Failed'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Quests</h2>
          <p className="text-xs text-zinc-500 mt-1">{quests.length} quest{quests.length !== 1 ? 's' : ''} configured</p>
        </div>
        <button
          data-testid="admin-add-quest-btn"
          onClick={() => { resetForm(); setShowForm(true); }}
          className="honey-btn px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2"
        >
          <Plus className="w-3.5 h-3.5" />
          Create Quest
        </button>
      </div>

      {/* Quest Cards */}
      <div className="grid gap-4">
        {quests.map((q, i) => {
          const TypeIcon = QUEST_TYPE_ICONS[q.quest_type] || Swords;
          return (
            <motion.div
              key={q.id}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="glass-card rounded-xl p-5"
              data-testid={`admin-quest-${i}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    q.status === 'active' ? 'bg-amber-500/10' : 'bg-zinc-800'
                  }`}>
                    <TypeIcon className={`w-5 h-5 ${q.status === 'active' ? 'text-amber-500' : 'text-zinc-600'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-bold text-white">{q.title}</h3>
                      <Badge variant="outline" className={`text-[9px] ${
                        q.status === 'active' ? 'border-emerald-500/30 text-emerald-400' : 'border-zinc-700 text-zinc-500'
                      }`}>
                        {q.status}
                      </Badge>
                      <Badge variant="outline" className="text-[9px] border-purple-500/30 text-purple-400">
                        {QUEST_TYPE_LABELS[q.quest_type]}
                      </Badge>
                    </div>
                    <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{q.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-[10px] text-zinc-600">
                      <span className="font-mono text-amber-500 font-bold">+{q.points} pts</span>
                      <span>Limit: {q.per_user_limit}x</span>
                      {q.attached_code && <span className="text-purple-400">Code: {q.attached_code}</span>}
                      <span>{q.total_completions || 0} completed</span>
                      {q.pending_count > 0 && (
                        <span className="text-amber-400 font-bold">{q.pending_count} pending</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-2">
                  <button onClick={() => toggleQuest(q)} className={`p-2 rounded-md ${q.status === 'active' ? 'text-emerald-400 hover:bg-emerald-500/10' : 'text-zinc-500 hover:bg-zinc-800'}`} data-testid={`toggle-quest-${i}`}>
                    {q.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                  <button onClick={() => openEdit(q)} className="p-2 rounded-md text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10" data-testid={`edit-quest-${i}`}>
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => deleteQuest(q.id)} className="p-2 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10" data-testid={`delete-quest-${i}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {quests.length === 0 && !loading && (
        <div className="text-center py-16 glass-card rounded-xl">
          <Swords className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-sm text-zinc-500">No quests created yet</p>
          <p className="text-xs text-zinc-600 mt-1">Create quests to engage your users</p>
        </div>
      )}

      {/* Create/Edit Quest Dialog */}
      <Dialog open={showForm} onOpenChange={(v) => { if (!v) { setShowForm(false); resetForm(); } }}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">{editingQuest ? 'Edit Quest' : 'Create Quest'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-zinc-500">Title *</Label>
              <Input data-testid="quest-title-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="bg-[#09090B] border-white/10 text-white mt-1" required />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Description *</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="bg-[#09090B] border-white/10 text-white mt-1" required />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-zinc-500">Points *</Label>
                <Input data-testid="quest-points-input" type="number" value={form.points} onChange={e => setForm({ ...form, points: e.target.value })} className="bg-[#09090B] border-white/10 text-white mt-1" required />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Type *</Label>
                <Select value={form.quest_type} onValueChange={v => setForm({ ...form, quest_type: v })}>
                  <SelectTrigger data-testid="quest-type-select" className="bg-[#09090B] border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121214] border-white/10">
                    <SelectItem value="screenshot" className="text-white">Screenshot</SelectItem>
                    <SelectItem value="manual" className="text-white">Manual</SelectItem>
                    <SelectItem value="link_visit" className="text-white">Link Visit</SelectItem>
                    <SelectItem value="purchase" className="text-white">Purchase</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-500">User Limit</Label>
                <Input type="number" min="1" value={form.per_user_limit} onChange={e => setForm({ ...form, per_user_limit: e.target.value })} className="bg-[#09090B] border-white/10 text-white mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-500">Attached Code</Label>
                <Input value={form.attached_code} onChange={e => setForm({ ...form, attached_code: e.target.value.toUpperCase() })} placeholder="PROMO20" className="bg-[#09090B] border-white/10 text-white mt-1 font-mono" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Thumbnail URL</Label>
                <Input value={form.thumbnail_url} onChange={e => setForm({ ...form, thumbnail_url: e.target.value })} placeholder="https://..." className="bg-[#09090B] border-white/10 text-white mt-1" />
              </div>
            </div>

            {form.quest_type === 'link_visit' && (
              <div>
                <Label className="text-xs text-zinc-500">Link URL *</Label>
                <Input value={form.link_url} onChange={e => setForm({ ...form, link_url: e.target.value })} placeholder="https://twitter.com/honeypot" className="bg-[#09090B] border-white/10 text-white mt-1" required />
              </div>
            )}

            {form.quest_type === 'purchase' && (
              <div className="space-y-3 p-3 rounded-lg bg-white/[0.02] border border-white/5">
                <p className="text-xs font-bold text-zinc-400">Purchase Config</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-zinc-500">Min Order ($)</Label>
                    <Input type="number" step="0.01" value={form.min_order_value} onChange={e => setForm({ ...form, min_order_value: e.target.value })} className="bg-[#09090B] border-white/10 text-white mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-500">Discount %</Label>
                    <Input type="number" value={form.discount_percentage} onChange={e => setForm({ ...form, discount_percentage: e.target.value })} className="bg-[#09090B] border-white/10 text-white mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">Product URL</Label>
                  <Input value={form.product_url} onChange={e => setForm({ ...form, product_url: e.target.value })} className="bg-[#09090B] border-white/10 text-white mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">Promo Code</Label>
                  <Input value={form.promo_code} onChange={e => setForm({ ...form, promo_code: e.target.value.toUpperCase() })} className="bg-[#09090B] border-white/10 text-white mt-1 font-mono" />
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="flex-1 px-4 py-2 rounded-lg text-sm bg-white/5 text-zinc-400 hover:bg-white/10">Cancel</button>
              <button type="submit" data-testid="quest-submit-btn" disabled={saving} className="flex-1 honey-btn px-4 py-2 rounded-lg text-sm font-bold disabled:opacity-50">
                {saving ? 'Saving...' : editingQuest ? 'Update' : 'Create Quest'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
